import { Typography, createTheme } from "@mui/material";
import { createBreakpoints } from "@mui/system";
import React from "react";

const CustomDropdownIcon = () => {
  return (
    <Typography
      component="span"
      className="t-custom-dropdown t-icon-arrow-down"
      sx={{
        color: "#ED0000",
        position: "absolute",
        right: "12px",
        pointerEvents: "none",
      }}
    ></Typography>
  );
};

declare module "@mui/material" {
  interface TypographyPropsVariantOverrides {
    tab: true;
    navItem: true;
    link: true;
  }
  interface TypographyClasses {
    tab: string;
    navItem: true;
    link: true;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    default: string;
    electricity: string;
    gas: string;
    services: string;
    management: string;
    promo1: string;
    promo2: string;
  }
  interface TypeConsumptions {
    period1: { main: string; light: string };
    period2: { main: string; light: string };
    period3: string;
    period4: string;
    period5: string;
    period6: string;
  }
  interface Palette {
    electricity: string;
    gas: string;
    services: string;
    management: string;
    background: TypeBackground;
    consumptions: TypeConsumptions;
    selected: { main: string; light: string };
  }

  interface PaletteOptions {
    electricity: string;
    gas: string;
    services: string;
    management: string;
    background?: Partial<TypeBackground>;
    consumptions: TypeConsumptions;
    selected: { main: string; light: string };
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    tab: React.CSSProperties;
    navItem: React.CSSProperties;
    link: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    tab?: React.CSSProperties;
    navItem?: React.CSSProperties;
    link?: React.CSSProperties;
  }

  interface BreakpointOverrides {
    headerMobile: true;
  }
}

// -------- BREAKPOINTS
const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 375,
    md: 768,
    headerMobile: 1100,
    lg: 1440,
    xl: 1920,
  },
});

const theme = createTheme({
  breakpoints: breakpoints,

  // -------- TYPOGRAPHY
  typography: {
    // ---- H1
    h1: {
      fontFamily: "Nunito-Semibold",
      fontSize: "30px",
      lineHeight: "40px",
      fontWeight: 600,
      letterSpacing: "0.1px",
      [breakpoints.up("md")]: {
        fontSize: "36px",
        lineHeight: "48px",
      },
    },
    // ---- H2
    h2: {
      fontFamily: "Nunito",
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: 400,
      letterSpacing: "0.1px",
      [breakpoints.up("md")]: {
        fontSize: "28px",
        lineHeight: "40px",
      },
    },
    // ---- H3
    h3: {
      fontFamily: "Nunito-Semibold",
      fontSize: "18px",
      lineHeight: "32px",
      fontWeight: 600,
      letterSpacing: "0.1px",
      [breakpoints.up("md")]: {
        fontSize: "22px",
        lineHeight: "32px",
      },
    },
    // ---- Title
    subtitle1: {
      fontFamily: "Roboto",
      fontSize: "18px",
      lineHeight: "24px",
      fontWeight: 600,
      letterSpacing: "0.1px",
      [breakpoints.up("md")]: {
        fontSize: "18px",
        lineHeight: "24px",
      },
    },
    // ---- Subtitle
    subtitle2: {
      fontFamily: "Roboto",
      fontSize: "18px",
      lineHeight: "24px",
      fontWeight: 600,
      letterSpacing: "0.1px",
      [breakpoints.up("md")]: {
        fontSize: "18px",
        lineHeight: "24px",
      },
    },
    // ---- Body 1
    body1: {
      fontFamily: "Roboto",
      fontSize: "18px",
      lineHeight: "24px",
      fontWeight: 400,
      letterSpacing: "0.1px",
    },
    // ---- Body 2
    body2: {
      fontFamily: "Roboto",
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 400,
      letterSpacing: "0.1px",
    },
    // ---- Caption
    caption: {
      fontFamily: "Roboto",
      fontSize: "14px",
      lineHeight: "16px",
      fontWeight: 400,
      letterSpacing: "0.1px",
    },
    // ---- Button
    button: {
      fontFamily: "Roboto-Semibold",
      fontSize: "16px",
      lineHeight: "16px",
      fontWeight: 600,
      letterSpacing: "0.1px",
      textTransform: "none",
    },
    // ---- Tab
    tab: {
      fontFamily: "Nunito-Semibold",
      fontSize: "22px",
      lineHeight: "24px",
      fontWeight: 600,
      letterSpacing: "0.1px",
    },
    // ---- Nav Item
    navItem: {
      fontFamily: "Roboto-Semibold",
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 600,
      letterSpacing: "0.1px",
    },
    // ---- Link
    link: {
      fontFamily: "Roboto-Semibold",
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: 600,
      letterSpacing: "0.1px",
    },
  },

  // -------- COLORS
  palette: {
    primary: {
      main: "#ED0000",
    },
    grey: {
      50: "#F7F9FA",
      100: "#EFF3F5",
      200: undefined,
      300: "#A7B8BF",
      400: undefined,
      500: "#6B8893",
      600: undefined,
      700: "#405258",
      800: undefined,
      900: "#161C1F",
      A100: undefined,
      A200: undefined,
      A400: undefined,
      A700: undefined,
    },
    error: {
      main: "#DB3900",
      light: "#DB39001F",
      dark: undefined,
      contrastText: "#B22E00",
    },
    info: {
      main: "#FFE74F",
      light: "#FFE74F66",
      dark: undefined,
      contrastText: "#405258",
    },
    success: {
      main: "#57AE60",
      light: "#57AE6026",
      dark: undefined,
      contrastText: "#397A3F",
    },
    selected: {
      main: "#80CDFF",
      light: "#80CDFF26",
    },
    electricity:
      "linear-gradient(180deg, rgba(2,155,235,1) 0%, rgba(0,170,97,1) 100%)",
    gas: "linear-gradient(180deg, rgba(0,156,234,1) 0%, rgba(70,50,255,1) 100%)",
    management:
      "linear-gradient(180deg, rgba(0,156,234,1) 0%, rgba(70,50,255,1) 100%)",
    services:
      "linear-gradient(180deg, rgba(239,111,0,1) 0%, rgba(238,0,0,1) 100%)",
    // ---- Backgrounds
    background: {
      default: "#FFFFFF",
      electricity:
        "linear-gradient(180deg, rgba(2,155,235,1) 0%, rgba(0,170,97,1) 100%)",
      gas: "linear-gradient(180deg, rgba(0,156,234,1) 0%, rgba(70,50,255,1) 100%)",
      management:
        "linear-gradient(180deg, rgba(237, 0, 0, 1) 0%, rgba(237, 0, 0, 1) 100%)",
      services:
        "linear-gradient(180deg, rgba(239,111,0,1) 0%, rgba(238,0,0,1) 100%)",
      promo1: " linear-gradient(180deg, #009CEA 0%, #4632FF 100%)",
      promo2: " linear-gradient(180deg, #029BEB 0%, #00AA61 100%)",
    },
    consumptions: {
      period1: { main: "#285AFF", light: "#285AFF4D" },
      period2: { main: "#DB3900", light: "#DB39004D" },
      period3: "#FFE74F",
      period4: "#80CDFF",
      period5: "#F07001",
      period6: "#72EAC5",
    },
  },

  // ---- Components
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&::before": {
            height: "0",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: "0",
          "&.Mui-expanded": {
            minHeight: "3rem",
          },
        },
        content: {
          margin: "0",
          "&.Mui-expanded": {
            margin: "0",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: `
        font-family: 'Nunito-Semibold',
        font-size: '22px',
        line-height: '26px',
        font-weight: 600,
        letter-spacing: '0.1px',
      `,
    },
    MuiContainer: {
      styleOverrides: {
        maxWidthLg: {
          [breakpoints.up("lg")]: {
            maxWidth: "1264px",
          },
          [breakpoints.up("xl")]: {
            maxWidth: "1744px",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          border: 0,
          background: "#FFFFFF",
          boxShadow: "0px 6px 16px 0px rgba(55, 70, 73, 0.10)",
          borderRadius: 8,
          gap: 24,
          display: "flex",
          alignItems: "center",
          margin: "1.5rem 0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          border: 0,
          padding: "24px 16px",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textTransform: "unset",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: CustomDropdownIcon,
      },
      styleOverrides: {
        select: {
          borderColor: "#A7B8BF",
          backgroundColor: "#fff",
          padding: "12px",
          "&.Mui-focused": {
            borderColor: "#A7B8BF",
          },
          "&:focus-visible": {
            outline: "2px solid #80CDFF",
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "unset",
          },
        },
        asterisk: {
          color: "#ED0000",
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          input: {
            padding: "12px",
            boxShadow: "none",
          },
          "& input::": {
            "&-webkit-outer-spin-button, &-webkit-inner-spin-button": {
              display: "none",
            },
          },
        },
        notchedOutline: {
          borderColor: "#A7B8BF",
          borderWidth: "1px",
          "&:focus-visible": {
            outline: "2px solid #80CDFF",
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          color: "#6B8893",
          padding: 0,
          "&.Mui-focusVisible": {
            outline: "2px solid #80CDFF",
            outlineOffset: "2px",
            borderRadius: "2px",
          },
        },
      },
    },
    MuiTouchRipple: {
      styleOverrides: {
        root: {
          padding: 0,
          "&:focus-visible": {
            outline: "2px solid #80CDFF",
            outlineOffset: "2px",
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: "#161C1F",
          "&.Mui-selected": {
            backgroundColor: "#A7B8BF",
            "&:hover": {
              backgroundColor: "#A7B8BF",
            },
          },
          "&:hover": {
            backgroundColor: "#EFF3F5",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          "&.Mui-selected": {
            color: "#ed0000",
          },
        },
        textColorPrimary: {
          color: "#405258",
          "&:hover": {
            color: "#ed0000",
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          [breakpoints.down("md")]: {
            "& .MuiToolbar-root": {
              flexFlow: "wrap",
              paddingLeft: 0,

              "& .MuiTablePagination-selectLabel": {
                textAlign: "end",
                flex: "70%",
                paddingBottom: "2rem",
              },

              "& .MuiTablePagination-displayedRows": {
                textAlign: "end",
                flex: "40%",
              },

              "@media (max-width: 435px)": {
                "& .MuiTablePagination-displayedRows": {
                  textAlign: "end",
                  flex: "0%",
                },
              },
            },

            "& .MuiInputBase-root": {
              marginRight: 0,
              paddingBottom: "2rem",
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        outlinedSuccess: {
          backgroundColor: "rgba(87, 174, 96, 0.15)",
          color: "#397A3F",
        },
        outlinedError: {
          backgroundColor: "rgba(219, 57, 0, 0.12)",
          color: "#B22E00",
        },
        outlinedWarning: { undefined },
        outlinedInfo: {
          backgroundColor: "rgba(255, 231, 79, 0.4)",
          color: "#405258",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true, // No more ripple, on the whole application 💣!
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            backgroundColor: "#A7B8BF",
            color: "#405258",
            borderColor: "#A7B8BF",
          },
          "&.Mui-focusVisible": {
            "&.attached-file-button": {
              backgroundColor: "#80cdff26",
              border: "1px dashed #80CDFF",
            },
          },
        },
      },
    },
  },
});

export default theme;
